import { forwardRef } from 'react';
import type { SelectHTMLAttributes, ReactNode } from 'react';
import { ChevronDown, AlertCircle } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /**
   * Etiqueta del select
   */
  label?: string;
  /**
   * Contenido adicional en el label (tooltips, badges, etc.)
   */
  labelAction?: ReactNode;
  /**
   * Si el campo es requerido
   */
  required?: boolean;
  /**
   * Mensaje de error a mostrar
   */
  error?: string;
  /**
   * Texto de ayuda debajo del select
   */
  helperText?: string;
  /**
   * Opciones del select
   */
  options?: SelectOption[];
  /**
   * Texto placeholder para primera opción
   */
  placeholder?: string;
  /**
   * Tamaño del select
   */
  /** `auto`: 32 px por debajo de 1536 px y 40 px desde ahí. */
  size?: 'small' | 'medium' | 'large' | 'auto';
  /**
   * Clases adicionales para el contenedor
   */
  containerClassName?: string;
  /**
   * ID único para accesibilidad
   */
  id?: string;
  /**
   * Icono a mostrar a la derecha del select
   */
  rightIcon?: ReactNode;
  /**
   * Si el campo es de solo lectura (no editable, pero sin apariencia deshabilitada)
   */
  readOnly?: boolean;
}

/**
 * Componente Select reutilizable
 * 
 * Campo de selección de opciones con soporte para validaciones y diferentes estados.
 * Compatible con dark mode mediante CSS variables.
 * 
 * @example
 * ```tsx
 * <Select
 *   label="País"
 *   placeholder="Seleccionar país"
 *   options={[
 *     { value: 'pe', label: 'Perú' },
 *     { value: 'cl', label: 'Chile' }
 *   ]}
 *   required
 * />
 * ```
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(({
  label,
  labelAction,
  required = false,
  error,
  helperText,
  options = [],
  placeholder,
  size = 'medium',
  containerClassName = '',
  className = '',
  id,
  disabled,
  readOnly = false,
  children,
  ...props
}, ref) => {
  const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
  
  // Clases para el tamaño - SIN line-height para evitar desalineación
  const sizeClasses = {
    small: 'h-8 text-ui-sm',
    medium: 'h-10 text-ui-base',
    large: 'h-12 text-body',
    auto: 'h-8 text-ui-sm 2xl:h-10 2xl:text-ui-base'
  };
  
  // Margen del label según tamaño
  const labelMargin = size === 'small' ? 'mb-1' : 'mb-1.5';
  
  return (
    <div className={containerClassName}>
      {/* Label */}
      {label && (
        <label 
          htmlFor={selectId}
          className={`block text-ui-sm font-medium ${labelMargin} text-tertiary`}
        >
          <span className="flex items-center gap-2">
            <span>{label} {required && <span className="text-error">*</span>}</span>
            {labelAction}
          </span>
        </label>
      )}
      
      {/* Select Container */}
      <div className="relative">
        <select
          ref={ref}
          id={selectId}
          disabled={disabled}
          className={`w-full ${sizeClasses[size]} pl-4 leading-none font-sans border rounded-lg transition-all duration-200 focus:outline-none appearance-none ${placeholder && (props.value === '' || props.value === undefined) ? 'text-muted' : 'text-primary'} ${
            readOnly
              ? 'pr-4 cursor-default pointer-events-none bg-surface-2 border-strong text-secondary'
              : 'pr-11 hover:border-strong cursor-pointer bg-surface-0 focus:ring-2 focus:ring-primary'
          } disabled:opacity-50 disabled:cursor-not-allowed ${
            error ? 'border-error' : 'border-strong'
          } ${className}`}
          aria-label={label || props['aria-label']}
          aria-invalid={!!error}
          aria-describedby={error ? `${selectId}-error` : helperText ? `${selectId}-helper` : undefined}
          {...props}
        >
          {placeholder && (
            <option value="">{placeholder}</option>
          )}
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              style={{ color: option.disabled ? undefined : 'initial' }}
            >
              {option.label}
            </option>
          ))}
          {children}
        </select>
        
        {/* Chevron Icon */}
        {!readOnly && (
          <ChevronDown
            size={18}
            className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-tertiary"
          />
        )}
      </div>
      
      {/* Error Message */}
      {error && (
        <div 
          className="flex items-center gap-1.5 text-caption text-error mt-2" 
          id={`${selectId}-error`}
        >
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}
      
      {/* Helper Text */}
      {!error && helperText && (
        <div 
          className="text-caption mt-2 text-tertiary" 
          id={`${selectId}-helper`}
        >
          {helperText}
        </div>
      )}
    </div>
  );
});

Select.displayName = 'Select';


